import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/auth-context";

const WEEKLY_SECURITY_DISMISS_KEY = "logvi_weekly_session_renew_dismissed";

/**
 * Deixe true apenas para testar visualmente.
 * Depois de validar o layout, altere para false.
 */
const FORCE_SHOW_FOR_TEST = false;

const CLOSE_ANIMATION_MS = 220;

/**
 * Regra de segurança:
 * o banner só aparece se a sessão estiver ativa há 5 dias ou mais.
 */
const MIN_SESSION_DAYS_TO_SHOW = 5;

function getCurrentWeekKey() {
  const now = new Date();
  const year = now.getFullYear();

  const firstDayOfYear = new Date(year, 0, 1);
  const pastDaysOfYear = Math.floor(
    (now.getTime() - firstDayOfYear.getTime()) / 86_400_000
  );

  const week = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);

  return `${year}-W${String(week).padStart(2, "0")}`;
}

function isMonday() {
  return new Date().getDay() === 1;
}

function removeAccents(value: string) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function norm(value: string) {
  return removeAccents(String(value || ""))
    .toLowerCase()
    .trim();
}

function slugify(value: string) {
  return norm(value)
    .replace(/&/g, "e")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getDeptRouteKey(dept: any) {
  if (dept?.routeKey && String(dept.routeKey).trim()) {
    return String(dept.routeKey).trim().replace(/^\/+/, "");
  }

  if (dept?.route_key && String(dept.route_key).trim()) {
    return String(dept.route_key).trim().replace(/^\/+/, "");
  }

  if (dept?.slug && String(dept.slug).trim()) {
    return String(dept.slug).trim().replace(/^\/+/, "");
  }

  return slugify(dept?.nome || "");
}

function getDeptThemeColor(dept: any) {
  const color =
    dept?.themeColor ||
    dept?.theme_color ||
    dept?.cor_tema ||
    dept?.cor ||
    null;

  return typeof color === "string" && color.trim() ? color.trim() : null;
}

function hexToRgb(hex: string): [number, number, number] | null {
  const clean = String(hex || "")
    .replace("#", "")
    .trim();

  if (!/^[0-9a-fA-F]{6}$/.test(clean)) {
    return null;
  }

  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

function darkenColor(color: string, amount = 34) {
  if (!color.startsWith("#")) {
    return color;
  }

  const rgb = hexToRgb(color);

  if (!rgb) {
    return color;
  }

  const [r, g, b] = rgb;

  return `rgb(${Math.max(0, r - amount)}, ${Math.max(
    0,
    g - amount
  )}, ${Math.max(0, b - amount)})`;
}

function getSessionActiveDays(ts?: number | null) {
  if (ts === undefined || ts === null) {
    return null;
  }

  const rawTs = Number(ts);

  if (!Number.isFinite(rawTs) || rawTs <= 0) {
    return null;
  }

  /**
   * Aceita timestamp em segundos ou milissegundos.
   * Exemplo:
   * - segundos: 1715000000
   * - milissegundos: 1715000000000
   */
  const sessionStartedAt = rawTs < 10_000_000_000 ? rawTs * 1000 : rawTs;

  const diffMs = Date.now() - sessionStartedAt;

  if (diffMs < 0) {
    return null;
  }

  return Math.max(0, Math.floor(diffMs / 86_400_000));
}

const WeeklySessionRenewBanner: React.FC = () => {
  const { logout, sessao } = useAuth();
  const location = useLocation();

  const [visible, setVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const closeTimerRef = useRef<number | null>(null);

  const currentWeekKey = useMemo(() => getCurrentWeekKey(), []);

  const activeDepartmentColor = useMemo(() => {
    const pathDept = location.pathname.split("/").filter(Boolean)[0];

    if (!pathDept) {
      return "#111827";
    }

    const departments = Array.isArray(sessao?.departamentos_acessiveis)
      ? sessao?.departamentos_acessiveis
      : [];

    const matchedDept = departments?.find((dept: any) => {
      const routeKey = getDeptRouteKey(dept);
      const slug = String(dept?.slug || "");
      const nome = String(dept?.nome || "");

      return (
        norm(routeKey) === norm(pathDept) ||
        norm(slug) === norm(pathDept) ||
        slugify(nome) === norm(pathDept)
      );
    });

    return getDeptThemeColor(matchedDept) || "#111827";
  }, [location.pathname, sessao?.departamentos_acessiveis]);

  const activeDepartmentButtonColor = useMemo(() => {
    return darkenColor(activeDepartmentColor, 42);
  }, [activeDepartmentColor]);

  const activeDepartmentHoverColor = useMemo(() => {
    return darkenColor(activeDepartmentColor, 58);
  }, [activeDepartmentColor]);

  const sessionActiveDays = useMemo(() => {
    return getSessionActiveDays(sessao?.ts ?? null);
  }, [sessao?.ts]);

  const sessionActiveText = useMemo(() => {
    if (sessionActiveDays === null) {
      return "Identificamos que seu acesso permanece ativo há algum tempo.";
    }

    if (sessionActiveDays === 0) {
      return "Identificamos que seu acesso foi iniciado hoje.";
    }

    if (sessionActiveDays === 1) {
      return "Identificamos que seu acesso está ativo há 1 dia.";
    }

    return `Identificamos que seu acesso está ativo há ${sessionActiveDays} dias.`;
  }, [sessionActiveDays]);

  useEffect(() => {
    if (!sessao) {
      setVisible(false);
      setIsClosing(false);
      return;
    }

    const shouldShowByDay = FORCE_SHOW_FOR_TEST || isMonday();

    if (!shouldShowByDay) {
      setVisible(false);
      setIsClosing(false);
      return;
    }

    const hasSessionAge =
      typeof sessionActiveDays === "number" && sessionActiveDays >= 0;

    const shouldShowBySessionAge =
      FORCE_SHOW_FOR_TEST ||
      !hasSessionAge ||
      sessionActiveDays >= MIN_SESSION_DAYS_TO_SHOW;

    if (!shouldShowBySessionAge) {
      setVisible(false);
      setIsClosing(false);
      return;
    }

    try {
      const dismissedWeek = localStorage.getItem(WEEKLY_SECURITY_DISMISS_KEY);

      if (dismissedWeek !== currentWeekKey || FORCE_SHOW_FOR_TEST) {
        setVisible(true);
        setIsClosing(false);
      }
    } catch {
      setVisible(true);
      setIsClosing(false);
    }
  }, [sessao, currentWeekKey, sessionActiveDays]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const closeWithAnimation = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }

    setIsClosing(true);

    closeTimerRef.current = window.setTimeout(() => {
      setVisible(false);
      setIsClosing(false);
      closeTimerRef.current = null;
    }, CLOSE_ANIMATION_MS);
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(WEEKLY_SECURITY_DISMISS_KEY, currentWeekKey);
    } catch {
      // ignora falha local
    }

    closeWithAnimation();
  };

  const handleRenewAccess = async () => {
    try {
      localStorage.removeItem(WEEKLY_SECURITY_DISMISS_KEY);
    } catch {
      // ignora falha local
    }

    await logout();
  };

  if (!visible) {
    return null;
  }

  return (
    <div
      style={{
        ...styles.banner,
        ...(isClosing ? styles.bannerClosing : {}),
      }}
    >
      <div style={styles.content}>
        <div style={styles.title}>Protocolo de segurança</div>

        <div style={styles.message}>
          <span style={styles.messageFirstLine}>{sessionActiveText}</span>

          <span>
            Sessões prolongadas podem aumentar o risco de acesso indevido em
            dispositivos compartilhados, navegadores salvos ou ambientes com
            múltiplos usuários. Recomendamos fortemente realizar um novo acesso
            à sua conta.
          </span>
        </div>
      </div>

      <div style={styles.actions}>
        <button
          type="button"
          style={{
            ...styles.primaryButton,
            background: activeDepartmentButtonColor,
            borderColor: activeDepartmentButtonColor,
          }}
          onClick={handleRenewAccess}
          disabled={isClosing}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = activeDepartmentHoverColor;
            e.currentTarget.style.borderColor = activeDepartmentHoverColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = activeDepartmentButtonColor;
            e.currentTarget.style.borderColor = activeDepartmentButtonColor;
          }}
        >
          Realizar novo acesso
        </button>

        <button
          type="button"
          style={styles.secondaryButton}
          onClick={handleDismiss}
          disabled={isClosing}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f3f4f6";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#ffffff";
          }}
        >
          Lembrar depois
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  banner: {
    position: "fixed",
    top: 74,
    left: "50%",
    transform: "translateX(-50%) translateY(0)",
    zIndex: 99999,
    width: "min(960px, calc(100vw - 48px))",
    minHeight: 62,
    padding: "12px 14px",
    borderRadius: 8,
    background: "#ffffff",
    border: "1px solid #d1d5db",
    boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    opacity: 1,
    transition:
      "opacity 220ms ease, transform 220ms ease, box-shadow 220ms ease",
    fontFamily:
      '"Nunito", "Inter", "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  bannerClosing: {
    opacity: 0,
    transform: "translateX(-50%) translateY(-10px)",
    boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
    pointerEvents: "none",
  },

  content: {
    minWidth: 0,
  },

  title: {
    marginBottom: 3,
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.35,
    textTransform: "uppercase",
    color: "#111827",
  },

  message: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.38,
    color: "#4b5563",
  },

  messageFirstLine: {
    display: "block",
    marginTop: 5,
    marginBottom: 2,
    fontWeight: 700,
    color: "#111827",
  },

  actions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },

  primaryButton: {
    height: 34,
    padding: "0 13px",
    borderRadius: 6,
    border: "1px solid #111827",
    background: "#111827",
    color: "#ffffff",
    fontSize: 12.5,
    fontWeight: 850,
    cursor: "pointer",
    transition: "all 140ms ease",
    whiteSpace: "nowrap",
  },

  secondaryButton: {
    height: 34,
    padding: "0 13px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#ffffff",
    color: "#111827",
    fontSize: 12.5,
    fontWeight: 850,
    cursor: "pointer",
    transition: "all 140ms ease",
    whiteSpace: "nowrap",
  },
};

export default WeeklySessionRenewBanner;
